import React, { useState, useEffect } from 'react';
import { 
  Dog, 
  Search, 
  Clock,
  X,
  Calendar,
  LogOut,
  Calendar as CalendarIcon,
  User
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS, getAuthHeaders } from '../config/api.ts';
import ProtectedData from '../components/ProtectedData.tsx';

interface Owner {
  _id: string;
  name: string;
  surname: string;
  email: string;
  phone: string;
}

interface CompletedCheckin {
  id: string;
  dog: {
    _id: string;
    name: string;
    breed: string;
    profileImage?: string;
    owner: {
      _id: string;
      name: string;
      surname: string;
      email: string;
      phone: string;
    };
  } | null;
  branch: {
    _id: string;
    name: string;
  };
  staff: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    position: string;
  };
  checkoutStaff?: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    position: string;
  };
  serviceType: 'hotel' | 'daycare';
  checkInTime: string;
  checkOutTime: string;
  status: string;
  notes?: string;
  duration?: number;
  totalTime?: number;
}

const SERVICE_TYPES = {
  hotel: { label: 'Otel', icon: '🏨', color: 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white' },
  daycare: { label: 'Kreş', icon: '🏫', color: 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' }
};

const GecmisZiyaretler: React.FC = () => {
  const [completedCheckins, setCompletedCheckins] = useState<CompletedCheckin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [selectedCheckin, setSelectedCheckin] = useState<CompletedCheckin | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const itemsPerPage = 12; // 3x4 grid için uygun

  const fetchCompletedCheckins = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);
      
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.CHECKIN_COMPLETED_STAFF}`,
        {
          method: 'GET',
          headers,
        }
      );

      // Check for token expiration
      if (response.status === 401) {
        const { handleTokenExpired } = await import('../config/api');
        handleTokenExpired(response);
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        setCompletedCheckins(data.data || []);
      } else {
        throw new Error(data.message || 'Geçmiş ziyaretler getirilemedi');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bilinmeyen hata');
      setCompletedCheckins([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompletedCheckins();
  }, []);

  // İstatistikleri hesapla
  const stats = {
    total: completedCheckins.length,
    hotel: completedCheckins.filter(checkin => checkin.serviceType === 'hotel').length,
    daycare: completedCheckins.filter(checkin => checkin.serviceType === 'daycare').length
  };

  // Filtreleme
  const filteredCheckins = completedCheckins.filter(checkin => {
    // Null safety check for checkin.dog
    if (!checkin.dog) {
      return false; // Skip checkins with null dog
    }
    
    const matchesSearch = 
      checkin.dog.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (checkin.dog.owner && checkin.dog.owner.name && checkin.dog.owner.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (checkin.dog.owner && checkin.dog.owner.surname && checkin.dog.owner.surname.toLowerCase().includes(searchTerm.toLowerCase())) ||
      checkin.serviceType.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCheckins.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCheckins.length / itemsPerPage);

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDuration = (duration: number) => {
    if (duration < 24) {
      return `${duration} saat`;
    } else {
      const days = Math.floor(duration / 24);
      const hours = duration % 24;
      return `${days} gün ${hours} saat`;
    }
  };

  const handleCheckinClick = (checkin: CompletedCheckin) => {
    setSelectedCheckin(checkin);
    setShowDetails(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Geçmiş ziyaretler yükleniyor...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">Geçmiş Ziyaretler</h1>
              <p className="text-gray-600 mt-1">Tamamlanan check-in'ler</p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Toplam Ziyaret */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:border-orange-200 hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Toplam Ziyaret</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-orange-200 to-amber-200 rounded-lg flex items-center justify-center">
                  <Calendar size={24} className="text-orange-600" />
                </div>
              </div>
            </div>

            {/* Otel Ziyaretleri */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:border-orange-200 hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Otel Ziyaretleri</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.hotel}</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-orange-200 to-amber-200 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🏨</span>
                </div>
              </div>
            </div>

            {/* Kreş Ziyaretleri */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:border-orange-200 hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Kreş Ziyaretleri</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.daycare}</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-orange-200 to-amber-200 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🏫</span>
                </div>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-orange-400" size={20} />
                <input
                  type="text"
                  placeholder="Köpek adı, sahip adı veya hizmet türü ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2">
              <span className="text-red-800 font-bold">HATA: {error}</span>
            </div>
          </div>
        )}

        {/* Checkins Grid */}
        {currentItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Geçmiş Ziyaret Bulunamadı</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm ? 'Arama kriterlerinize uygun geçmiş ziyaret bulunamadı.' : 'Şu anda tamamlanan check-in bulunmuyor.'}
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-left">
              <h4 className="font-medium text-orange-800 mb-2">💡 Bilgi:</h4>
              <p className="text-orange-700 text-sm">
                Bu sayfa sadece tamamlanan (check-out yapılmış) ziyaretleri gösterir. 
                Şu anda aktif olan köpekler "Oteldeki Kedi/Köpekler" sayfasında görüntülenebilir.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {currentItems.map((checkin) => {
              // Skip rendering if dog is null
              if (!checkin.dog) {
                return null;
              }
              
              const serviceType = SERVICE_TYPES[checkin.serviceType];
              return (
                <div
                  key={checkin.id}
                  className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-[1.02] hover:border-orange-200 h-full flex flex-col group"
                  onClick={() => handleCheckinClick(checkin)}
                >
                  {/* Card Header */}
                  <div className="p-4 border-b border-gray-50 bg-gradient-to-r from-orange-100 to-amber-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-200 to-amber-200 rounded-full flex items-center justify-center ring-2 ring-white shadow-sm flex-shrink-0">
                          {checkin.dog.profileImage && !imageErrors.has(checkin.dog._id) ? (
                            <img 
                              src={checkin.dog.profileImage} 
                              alt={checkin.dog.name}
                              className="w-12 h-12 rounded-full object-cover ring-2 ring-white"
                              onError={() => {
                                setImageErrors(prev => new Set(prev).add(checkin.dog._id));
                              }}
                            />
                          ) : (
                            <Dog size={24} className="text-orange-600" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-bold text-gray-900 text-base truncate" title={checkin.dog.name}>
                            {checkin.dog.name}
                          </h3>
                          <p className="text-sm text-gray-600 font-medium truncate" title={checkin.dog.breed}>
                            {checkin.dog.breed}
                          </p>
                        </div>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full shadow-sm ${serviceType.color} ring-1 ring-white/50 flex-shrink-0 whitespace-nowrap`}>
                        {serviceType.icon} {serviceType.label}
                      </span>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-4 flex-1 flex flex-col bg-gradient-to-b from-white to-gray-50/30">
                    <div className="space-y-3 flex-1">
                      {/* Owner Info */}
                      <div className="flex items-center space-x-2 text-sm text-gray-700 bg-white/60 rounded-lg p-2 border border-gray-100">
                        <div className="w-6 h-6 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <User size={14} className="text-green-600" />
                        </div>
                        <span className="font-medium truncate flex-1 min-w-0">
                          {checkin.dog.owner ? `${checkin.dog.owner.name || ''} ${checkin.dog.owner.surname || ''}`.trim() : 'Silinen Kullanıcı'}
                        </span>
                      </div>

                      {/* Check-in Time */}
                      <div className="flex items-center space-x-2 text-sm text-gray-700 bg-white/60 rounded-lg p-2 border border-gray-100">
                        <div className="w-6 h-6 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center">
                          <Clock size={14} className="text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-xs">Giriş</p>
                          <p className="text-xs text-gray-500">{formatDate(checkin.checkInTime)} - {formatTime(checkin.checkInTime)}</p>
                        </div>
                      </div>

                      {/* Check-out Time */}
                      <div className="flex items-center space-x-2 text-sm text-gray-700 bg-white/60 rounded-lg p-2 border border-gray-100">
                        <div className="w-6 h-6 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full flex items-center justify-center">
                          <LogOut size={14} className="text-orange-500" />
                        </div>
                        <div>
                          <p className="font-medium text-xs">Çıkış</p>
                          <p className="text-xs text-gray-500">{formatDate(checkin.checkOutTime)} - {formatTime(checkin.checkOutTime)}</p>
                        </div>
                      </div>

                      {/* Check-out Staff */}
                      {checkin.checkoutStaff && (
                        <div className="flex items-center space-x-2 text-sm text-gray-700 bg-white/60 rounded-lg p-2 border border-gray-100">
                          <div className="w-6 h-6 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center">
                            <User size={14} className="text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-xs">Çıkış Yapan</p>
                            <p className="text-xs text-gray-500">{checkin.checkoutStaff.name} ({checkin.checkoutStaff.position})</p>
                          </div>
                        </div>
                      )}

                      {/* Duration */}
                      {checkin.duration && (
                        <div className="flex items-center space-x-2 text-sm text-gray-700 bg-white/60 rounded-lg p-2 border border-gray-100">
                          <div className="w-6 h-6 bg-gradient-to-br from-indigo-100 to-blue-100 rounded-full flex items-center justify-center">
                            <CalendarIcon size={14} className="text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-xs">Toplam Süre</p>
                            <p className="text-xs text-gray-500">{formatDuration(checkin.duration)}</p>
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {checkin.notes && (
                        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-2 border border-amber-100 mt-auto">
                          <div className="flex items-center space-x-2">
                            <div className="w-5 h-5 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-amber-600 text-xs">📝</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-amber-800 mb-1">Notlar</p>
                              <p className="text-xs text-amber-700 truncate" title={checkin.notes}>
                                {checkin.notes}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center space-x-2 mt-8">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-50 hover:border-orange-300"
            >
              Önceki
            </button>
            <span className="px-3 py-2 text-sm font-medium text-orange-500 bg-orange-50 rounded-lg">
              Sayfa {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-50 hover:border-orange-300"
            >
              Sonraki
            </button>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetails && selectedCheckin && selectedCheckin.dog && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-400 to-amber-400 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center ring-4 ring-white/30">
                    {selectedCheckin.dog.profileImage && !imageErrors.has(selectedCheckin.dog._id) ? (
                      <img 
                        src={selectedCheckin.dog.profileImage} 
                        alt={selectedCheckin.dog.name}
                        className="w-16 h-16 rounded-full object-cover"
                        onError={() => {
                          setImageErrors(prev => new Set(prev).add(selectedCheckin.dog._id));
                        }}
                      />
                    ) : (
                      <Dog size={32} className="text-white" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{selectedCheckin.dog.name}</h2>
                    <p className="text-orange-100">{selectedCheckin.dog.breed}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">
                        {SERVICE_TYPES[selectedCheckin.serviceType].icon} {SERVICE_TYPES[selectedCheckin.serviceType].label}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-full transition-all duration-200"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(95vh-120px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Sol Kolon - Temel Bilgiler */}
                <div className="space-y-6">
                  {/* Sahip Bilgileri */}
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-100">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-amber-400 rounded-full flex items-center justify-center">
                        <User size={20} className="text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Sahip Bilgileri</h3>
                    </div>
                    <div className="space-y-3">
                      <ProtectedData
                        label="Ad Soyad"
                        value={selectedCheckin.dog.owner ? `${selectedCheckin.dog.owner.name || ''} ${selectedCheckin.dog.owner.surname || ''}`.trim() : 'Silinen Kullanıcı'}
                        type="name"
                      />
                      <ProtectedData
                        label="E-posta"
                        value={selectedCheckin.dog.owner ? selectedCheckin.dog.owner.email : 'Hesap silindi'}
                        type="email"
                        dogId={selectedCheckin.dog._id}
                        ownerId={selectedCheckin.dog.owner ? selectedCheckin.dog.owner._id : undefined}
                        pageContext="gecmis-ziyaretler"
                      />
                      <ProtectedData
                        label="Telefon"
                        value={selectedCheckin.dog.owner ? selectedCheckin.dog.owner.phone : 'Hesap silindi'}
                        type="phone"
                        dogId={selectedCheckin.dog._id}
                        ownerId={selectedCheckin.dog.owner ? selectedCheckin.dog.owner._id : undefined}
                        pageContext="gecmis-ziyaretler"
                      />
                    </div>
                  </div>

                  {/* Ziyaret Süresi */}
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-100">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-amber-400 rounded-full flex items-center justify-center">
                        <CalendarIcon size={20} className="text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Ziyaret Süresi</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 font-medium">Giriş:</span>
                        <span className="text-gray-900 font-semibold">{formatDate(selectedCheckin.checkInTime)} - {formatTime(selectedCheckin.checkInTime)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 font-medium">Çıkış:</span>
                        <span className="text-gray-900 font-semibold">{formatDate(selectedCheckin.checkOutTime)} - {formatTime(selectedCheckin.checkOutTime)}</span>
                      </div>
                      {selectedCheckin.duration && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 font-medium">Toplam:</span>
                          <span className="text-orange-500 font-bold text-lg">{formatDuration(selectedCheckin.duration)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sağ Kolon - Personel ve Notlar */}
                <div className="space-y-6">
                  {/* Personel Bilgileri */}
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-100">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-amber-400 rounded-full flex items-center justify-center">
                        <User size={20} className="text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Personel Bilgileri</h3>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-white/60 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800 mb-2">Giriş Yapan Personel</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Ad:</span>
                            <span className="text-gray-900 font-medium">{selectedCheckin.staff.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Pozisyon:</span>
                            <span className="text-gray-900">{selectedCheckin.staff.position}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">E-posta:</span>
                            <span className="text-gray-900 text-sm">{selectedCheckin.staff.email}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Telefon:</span>
                            <span className="text-gray-900">{selectedCheckin.staff.phone}</span>
                          </div>
                        </div>
                      </div>
                      
                      {selectedCheckin.checkoutStaff && (
                        <div className="bg-white/60 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-800 mb-2">Çıkış Yapan Personel</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Ad:</span>
                              <span className="text-gray-900 font-medium">{selectedCheckin.checkoutStaff.name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Pozisyon:</span>
                              <span className="text-gray-900">{selectedCheckin.checkoutStaff.position}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">E-posta:</span>
                              <span className="text-gray-900 text-sm">{selectedCheckin.checkoutStaff.email}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Telefon:</span>
                              <span className="text-gray-900">{selectedCheckin.checkoutStaff.phone}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notlar */}
                  {selectedCheckin.notes && (
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-100">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-amber-400 rounded-full flex items-center justify-center">
                          <span className="text-white text-lg">📝</span>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Notlar</h3>
                      </div>
                      <div className="bg-white/60 rounded-lg p-4">
                        <p className="text-gray-800 leading-relaxed">{selectedCheckin.notes}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GecmisZiyaretler;
